/**
 * Progress for the signed-in learner, fetched once and shared.
 *
 * The catalogue cards and My Learning both need the same rows; fetching in each
 * place would double the query and let the two views disagree on screen after a
 * lesson is watched.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchMyProgress, type LessonProgress } from '../../services/learnerAPI';

export interface LearnerProgressState {
  byToken: Map<string, LessonProgress>;
  items: LessonProgress[];
  loading: boolean;
  failed: boolean;
  reload: () => void;
}

export function useLearnerProgress(): LearnerProgressState {
  const [items, setItems] = useState<LessonProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchMyProgress()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch(() => {
        // Progress is an enhancement over the catalogue, never a gate: on
        // failure the pages still render, just without status chips.
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    items,
    byToken: new Map(items.map((i) => [i.shareToken, i])),
    loading,
    failed,
    reload,
  };
}

export function formatWatchTime(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

export function formatRelativeDate(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
